import type { StockLocationsAPIResponse } from '../../hooks/use-stock-locations';
import type { PriceList } from '../price-lists';
import {
  CUSTOMER_GROUP_BASE_SLUG,
  DEFAULT_ALLOW_CHECKOUT,
  DEFAULT_MULTI_STOCK,
  DEFAULT_PAYMENT_METHODS,
  DEFAULT_PAYMENT_ON_ACCOUNT_ENFORCE_CREDIT_LIMIT,
  DEFAULT_PRICE_LIST_SELECTION,
  DEFAULT_QUOTING_ENGINE,
  DEFAULT_STOCK_DISPLAY,
  DEFAULT_USE_MULTI_LOCATION_STOCK,
  EXCLUDED_COMPONENT_SETTINGS,
  LIST_DELIMITER,
  SettingsDefaultsMap,
  TABLE_OTHER_SETTINGS,
} from './constants';
import { displaySettings, getSelectedPriceLists } from './formatter';
import type {
  CustomerGroupBuilderSettingsState,
  CustomerGroupBuilderSettingsValidated,
  SettingPriceListState,
} from './types/customer-group.schema';
import type {
  CustomerGroup,
  CustomerGroupApiResponse,
  FormattedSettingItem,
  InheritList,
  SettingResponseItem,
} from './types/customer-group-response';
import type { SettingComponentMapKeys, SettingKey } from './types/settings.types';

export function transformSettingsToApiResponse(
  settings: CustomerGroupBuilderSettingsValidated,
): SettingResponseItem[] {
  return Object.entries(settings).map(
    ([key, value]) =>
      ({
        key: key,
        value: value,
      }) as SettingResponseItem,
  );
}

export type SettingComponentObject = {
  key: SettingComponentMapKeys;
  value: CustomerGroupBuilderSettingsState[keyof CustomerGroupBuilderSettingsState];
};

export function filterSettings(
  settings?: CustomerGroupBuilderSettingsState,
): SettingComponentObject[] {
  if (!settings) {
    return [];
  }

  return (Object.keys(settings) as Array<keyof CustomerGroupBuilderSettingsState>)
    .filter((key): key is SettingComponentMapKeys => {
      const excluded: readonly SettingKey[] = EXCLUDED_COMPONENT_SETTINGS;
      return !excluded.includes(key);
    })
    .map((key) => ({
      key: key,
      value: settings[key],
    }));
}

export function transformSetting(setting: SettingResponseItem) {
  if (setting?.value === undefined || setting?.value === null) {
    return [setting.key, undefined];
  }

  // fetch API returns an empty array if empty object, so transform
  if (
    setting.key === 'orderItemQuantityValidation' &&
    Array.isArray(setting.value) &&
    setting.value.length === 0
  ) {
    return [setting.key, SettingsDefaultsMap[setting.key]];
  }

  // Transform numbers to strings
  if (setting.key === 'orderTotalsValidation') {
    return [
      setting.key,
      setting.value.map((v) => ({
        ...v,
        minimum_value: v.minimum_value?.toString(),
        maximum_value: v.maximum_value?.toString(),
      })),
    ];
  }

  if (setting.key === 'orderItemQuantityValidation') {
    return [
      setting.key,
      {
        ...setting.value,
        minimum_quantity: setting.value.minimum_quantity?.toString(),
        maximum_quantity: setting.value.maximum_quantity?.toString(),
      },
    ];
  }

  if (setting.key === 'stockDisplay') {
    return [
      setting.key,
      {
        ...setting.value,
        max: setting.value.max?.toString(),
        low: setting.value.low?.toString(),
        last: setting.value.last?.toString(),
      },
    ];
  }

  // Otherwise just return the setting
  return [setting.key, setting.value];
}

function findSetting<Type>(rowSettings: SettingResponseItem[], key: string) {
  const setting = rowSettings.find((x) => x.key === key) ?? null;
  return setting?.value as Type | null;
}

function getGroupPriceList(
  group?: CustomerGroup,
  priceLists?: PriceList[],
): PriceList[] | undefined {
  if (!group || !priceLists) {
    return undefined;
  }

  const settingsPriceList = findSetting<SettingPriceListState>(
    group.settings,
    'priceListSelection',
  );
  if (settingsPriceList) {
    return getSelectedPriceLists(settingsPriceList, priceLists);
  }

  return undefined;
}

export function getFormattedParentSettings(
  formattedSettings?: FormattedSettingItem[],
  formattedParentSettings?: FormattedSettingItem[],
): FormattedSettingItem[] | undefined {
  return formattedSettings?.map((setting) => {
    // if parent settings is undefined, check the the base customer group setting
    // if we have 2 levels of inheritance
    const value =
      setting.value ?? formattedParentSettings?.find((ps) => ps.key === setting.key)?.value;

    return {
      key: setting.key,
      value,
    };
  });
}

// normalizeTree goes through the group (tree structure based on children)
// and populates relevant settings based on the parent group, whilst keeping track of inheritance.
// Output: flattened customer groups.
function normalizeTree(group: CustomerGroup | undefined, level: number): CustomerGroup[] {
  if (!group) {
    return [];
  }

  let treeItem: CustomerGroup[] = [{ ...group, level }];
  if (group.children && group.children.length > 0) {
    for (let i = 0; i < group.children.length; i += 1) {
      const child = group.children[i];

      if (child === undefined) {
        continue;
      }

      const childWithInheritedSettings: CustomerGroup = {
        ...child,
        parent_name: group.name,
        // these subset of settings are for the table to quickly display settings
        priceLists: child.priceLists ?? [...(group.priceLists ?? [])],
        paymentMethods: child.paymentMethods ?? [...(group.paymentMethods ?? [])],
        orderTotalLimits: child.orderTotalLimits ?? [...(group.orderTotalLimits ?? [])],
        orderQuantityLimits: child.orderQuantityLimits ?? [...(group.orderQuantityLimits ?? [])],
        // formattedParentSettings is to show all the settings of the parent
        formattedParentSettings: getFormattedParentSettings(
          group.formattedSettings,
          group.formattedParentSettings,
        ),
      };

      treeItem = treeItem.concat(normalizeTree(childWithInheritedSettings, level + 1));
    }
  }

  return treeItem;
}

function formatCustomerGroup(
  groups: CustomerGroup[],
  priceLists?: PriceList[],
  stockLocations?: StockLocationsAPIResponse[],
  isQuotingEngineEnabled?: boolean,
  isMultiStockEnabled?: boolean,
): Record<string, CustomerGroup> {
  const map: Record<string, CustomerGroup> = {};

  groups.forEach((g) => {
    const allowDefaultBilling =
      g.settings.find((setting) => setting.key === 'allowDefaultBillingModification')?.value ??
      undefined;

    let formattedPaymentMethod: string[] | undefined,
      formattedOrderTotalLimits: string[] | undefined,
      formattedOrderQuantityLimits: string[] | undefined;

    const { formattedSettings, inheritList } = g.settings.reduce<{
      formattedSettings: FormattedSettingItem[];
      inheritList: InheritList;
    }>(
      (acc, setting) => {
        const formattedSettingValue = displaySettings(
          setting,
          priceLists,
          stockLocations,
          allowDefaultBilling,
          isQuotingEngineEnabled,
          isMultiStockEnabled,
        );

        switch (setting.key) {
          case 'allowedPaymentMethods':
            formattedPaymentMethod = formattedSettingValue?.split(LIST_DELIMITER) ?? undefined;
            break;
          case 'orderTotalsValidation':
            // Return an empty list if no setting value is found.
            formattedOrderTotalLimits =
              formattedSettingValue === '' ? [] : formattedSettingValue?.split(LIST_DELIMITER);
            break;
          case 'orderItemQuantityValidation':
            formattedOrderQuantityLimits = formattedSettingValue
              ? [formattedSettingValue]
              : undefined;
        }

        acc.formattedSettings.push({
          key: setting.key,
          value: formattedSettingValue,
        });

        if (setting.value === null) {
          acc.inheritList.push(setting.key);
        }

        return acc;
      },
      { formattedSettings: [], inheritList: [] },
    );

    // if base customer, also check against the order total and quantity limits
    const otherSettings: InheritList =
      g.slug === CUSTOMER_GROUP_BASE_SLUG
        ? [...TABLE_OTHER_SETTINGS, 'orderItemQuantityValidation', 'orderTotalsValidation']
        : TABLE_OTHER_SETTINGS;

    // get TABLE_OTHER_SETTINGS settingKeys and compare aginst settings that are not inherited, but are set.
    const overwrittenSettingsCounter = otherSettings.filter(
      (settingKey) =>
        !inheritList.includes(settingKey) &&
        g.settings.find((setting) => setting.key === settingKey)?.value !== undefined,
    ).length;

    if (TABLE_OTHER_SETTINGS.every((setting) => inheritList?.includes(setting))) {
      inheritList.push('overwrittenSettings');
    }

    map[g.slug] = {
      ...g,
      children: [],
      priceLists: getGroupPriceList(g, priceLists),
      paymentMethods: formattedPaymentMethod,
      orderTotalLimits: formattedOrderTotalLimits,
      orderQuantityLimits: formattedOrderQuantityLimits,
      overwrittenSettings: overwrittenSettingsCounter,
      inheritList: g.slug === CUSTOMER_GROUP_BASE_SLUG ? [] : inheritList,
      formattedSettings: formattedSettings,
    };
  });

  return map;
}
// TODO: think of a better way to handle this
// Duplicate of SettingsDefaultsMap, as those are for the builder form, not the API response.
// Values from spark-api/www/blu/Services/Settings/descriptors.php
const CUSTOMER_GROUP_PARENT_SLUG = 'default';

const DEFAULT_CUSTOMER_GROUP: CustomerGroup = {
  slug: CUSTOMER_GROUP_PARENT_SLUG,
  parent_slug: '',
  name: CUSTOMER_GROUP_PARENT_SLUG,
  settings: [
    { key: 'priceListSelection', value: DEFAULT_PRICE_LIST_SELECTION },
    { key: 'orderTotalsValidation', value: [] },
    { key: 'orderItemQuantityValidation', value: {} },
    {
      key: 'allowedPaymentMethods',
      value: DEFAULT_PAYMENT_METHODS,
    },
    { key: 'allowAddressEditing', value: false },
    {
      key: 'allowDefaultBillingModification',
      value: false,
    },
    {
      key: 'paymentOnAccountEnforceCreditLimit',
      value: DEFAULT_PAYMENT_ON_ACCOUNT_ENFORCE_CREDIT_LIMIT,
    },
    {
      key: 'stockDisplay',
      value: {
        ...DEFAULT_STOCK_DISPLAY,
        max: 100,
        low: 20,
        last: 5,
      },
    },
    { key: 'quotingEngine', value: DEFAULT_QUOTING_ENGINE },
    { key: 'allowCheckout', value: DEFAULT_ALLOW_CHECKOUT },
    {
      key: 'multiLocationStockConfig',
      value: DEFAULT_MULTI_STOCK,
    },
    { key: 'useMultiLocationStock', value: DEFAULT_USE_MULTI_LOCATION_STOCK },
  ],
};

export function buildHierarchialGroup(
  initialGroups?: CustomerGroupApiResponse[],
  initialPriceList?: PriceList[],
  initialStockLocations?: StockLocationsAPIResponse[],
  isQuotingEngineEnabled?: boolean,
  isMultiStockEnabled?: boolean,
) {
  if (!initialGroups) {
    return [];
  }

  const filteredGroups = initialGroups.filter((group) => {
    return group.slug !== 'sales-agent';
  });

  filteredGroups.push(DEFAULT_CUSTOMER_GROUP);

  const formattedGroupsMap = formatCustomerGroup(
    filteredGroups,
    initialPriceList,
    initialStockLocations,
    isQuotingEngineEnabled,
    isMultiStockEnabled,
  );

  let baseGroup: CustomerGroup | undefined;

  filteredGroups.forEach((group) => {
    const node = formattedGroupsMap[group.slug];
    if (!node) {
      return;
    }

    if (group.slug === CUSTOMER_GROUP_PARENT_SLUG) {
      baseGroup = node;
    }

    if (formattedGroupsMap[group.parent_slug]) {
      formattedGroupsMap[group.parent_slug]?.children?.push(node);
    }
  });

  const groupsHierarchy = normalizeTree(baseGroup, -1).filter(
    (g) => g.slug !== CUSTOMER_GROUP_PARENT_SLUG,
  );

  return Object.values(groupsHierarchy);
}
